import shopifyGenericDefaults from '../shopify-generic/config.js';
import { packageSelectsOnChange, packageSelectsUpdateCallback } from './package-pairing-selects.js';

const isWinterPackagePage = window.location.pathname.includes('/collections/winter-packages');
const isWheelTirePackagePage =
  window.location.pathname.includes('/collections/wheel-tire-packages') || isWinterPackagePage;
const isWheelTirePackageLastStep = window.location.href.includes('PackagesMode=lastStep');

const baseFitmentFields = shopifyGenericDefaults.fitmentSearch.baseFields;

const wheelsFields = shopifyGenericDefaults.Widgets.find((w) => w.name === 'FacetBarWheels').fields;
const wheelsPackagesRearFields = wheelsFields
  .filter((f) => !f.includes('bolt_pattern'))
  .map((f) => `${f}_rear`);
const tiresFields = shopifyGenericDefaults.Widgets.find((w) => w.name === 'FacetBarTires').fields;
const tiresPackagesRearFields = tiresFields.map((f) => `${f}_rear`);

shopifyGenericDefaults.Widgets.map((w) => {
  if (w.name === 'FilterChips' || w.name === 'Facets') {
    if (w.ignoreFields) {
      w.ignoreFields.push(
        ...wheelsFields,
        ...wheelsPackagesRearFields,
        ...tiresFields,
        ...tiresPackagesRearFields,
      );
    } else {
      w.ignoreFields = [
        ...wheelsFields,
        ...wheelsPackagesRearFields,
        ...tiresFields,
        ...tiresPackagesRearFields,
      ];
    }
  }
  return w;
});

window.Convermax.tireFacetsPreselect = {
  ['tire_width']: '225',
  ['tire_aspect']: '40',
  ['wheel_tire_diameter']: '19',
  ['tire_width_rear']: '255',
  ['tire_aspect_rear']: '35',
  ['wheel_tire_diameter_rear']: '19',
};

window.Convermax.generatePairedVariants = (item) => {
  const variants = item?._variants;

  if (!Array.isArray(variants) || variants.length === 0) {
    return [];
  }

  const pairs = item?.front_rear_pairs_ids;
  if (!Array.isArray(pairs) || pairs.length === 0) {
    return variants;
  }

  // id -> variant
  const variantById = new Map();

  for (const variant of variants) {
    const id = variant?.id;

    if (typeof id !== 'string' || id.length === 0) {
      continue;
    }

    if (!variantById.has(id)) {
      variantById.set(id, variant);
    }
  }

  const result = [];

  for (const pairKey of pairs) {
    if (typeof pairKey !== 'string' || pairKey.length === 0) {
      continue;
    }

    if (pairKey.includes('|')) {
      const parts = pairKey.split('|');

      if (parts.length !== 2) {
        continue;
      }

      const [frontId, rearId] = parts;

      const frontVariant = variantById.get(frontId);
      const rearVariant = variantById.get(rearId);

      if (!frontVariant || !rearVariant) {
        continue;
      }

      result.push({
        ...frontVariant,
        ['variant_rear']: rearVariant,
      });
    } else {
      const singleVariant = variantById.get(pairKey);

      if (!singleVariant) {
        continue;
      }

      result.push(singleVariant);
    }
  }

  return result;
};

// use fallback to 1 max quantity if DynamicStockUpdatePlugin fails
window.Convermax.getPackageMaxAddToCartQuantity = (variant, variantRear) => {
  const frontMaxStock = variant.max_add_to_cart_quantity || 1;
  const rearMaxStock = variantRear ? variantRear.max_add_to_cart_quantity || 1 : frontMaxStock;

  return Math.min(frontMaxStock, rearMaxStock);
};

window.Convermax.getPackageDefaultQuantity = (variant, variantRear, isOutOfStock) => {
  const isStaggeredVariants = !!(variant && (variantRear || variant.HasIncompletePackageMatch));
  const setQuantity = !isOutOfStock ? (isStaggeredVariants ? 2 : 4) : 0;

  return Math.min(setQuantity, window.Convermax.getPackageMaxAddToCartQuantity(variant, variantRear));
};

window.Convermax.getPackagePricePerUnit = (variant, variantRear, retail = true) => {
  const frontPrice = retail ? variant.product_price || variant.price : variant.price;
  const rearPrice = variantRear
    ? retail
      ? variantRear.product_price || variantRear.price
      : variantRear.price
    : 0;

  return (frontPrice || 0) + (rearPrice || 0);
};

window.Convermax.updatePackageTotalPrice = (cartEl, variant, variantRear, quantity, context) => {
  if (!cartEl || !context) {
    return;
  }

  const retailPriceEl = cartEl.querySelector('.cm_package_variant-price-retail');
  if (retailPriceEl) {
    const retailPricePerUnit = window.Convermax.getPackagePricePerUnit(variant, variantRear);
    retailPriceEl.textContent = context.formatPrice(retailPricePerUnit * quantity);
  }

  const b2bPriceEl = cartEl.querySelector('.cm_package_variant-price-b2b');
  if (b2bPriceEl && variant.product_price) {
    const b2bPricePerUnit = window.Convermax.getPackagePricePerUnit(variant, variantRear, false);
    b2bPriceEl.textContent = context.formatPrice(b2bPricePerUnit * quantity);
  }
};

window.Convermax.handlePackageQtyChange = (event, variant, variantRear, context, { normalizeValue } = {}) => {
  const quantity = window.Convermax.syncQtySelectorButtons(event.currentTarget?.parentElement, {
    normalizeValue: normalizeValue ?? event.type === 'change',
  });

  if (quantity == null) {
    return;
  }

  const cartEl = event.target.closest('.cm_package_variant-cart');
  window.Convermax.updatePackageTotalPrice(cartEl, variant, variantRear, quantity, context);
};

window.Convermax.syncQtySelectorButtons = (container, { normalizeValue = true } = {}) => {
  const quantitySelectorInput = container?.querySelector('input[name="quantity"]');
  if (!quantitySelectorInput || quantitySelectorInput.disabled) {
    return;
  }

  const minQty = +quantitySelectorInput.getAttribute('min');
  const maxQty = +quantitySelectorInput.getAttribute('max');

  let currentQty = Number.parseInt(quantitySelectorInput.value, 10);
  if (!Number.isFinite(currentQty)) {
    currentQty = minQty;
  } else {
    currentQty = Math.min(maxQty, Math.max(minQty, currentQty));
  }

  // While the user is actively typing (input event) we must not rewrite the
  // field value. An intermediate empty value would be snapped back to min and
  // the next keystroke appended to it (e.g. clear -> "1" -> type "4" -> "14" ->
  // clamped to max). Value normalization happens on commit instead: change/blur,
  // +/- clicks, and add-to-cart.
  if (normalizeValue) {
    const normalizedValue = String(currentQty);
    if (quantitySelectorInput.value !== normalizedValue) {
      quantitySelectorInput.value = normalizedValue;
      quantitySelectorInput.setAttribute('value', normalizedValue);
    }
  }

  const minusButton = container.querySelector('.cm_package_variant-quantity_button_minus');
  const plusButton = container.querySelector('.cm_package_variant-quantity_button_plus');

  if (minusButton) {
    minusButton.disabled = currentQty <= minQty;
  }

  if (plusButton) {
    plusButton.disabled = currentQty >= maxQty;
  }

  return currentQty;
};

window.Convermax.handleQtySelector = (target, variant, variantRear, context) => {
  const quantityContainer = target.parentElement;
  const quantitySelectorInput = quantityContainer.querySelector('input[name="quantity"]');

  const currentQty = +quantitySelectorInput.value;
  const minQty = +quantitySelectorInput.getAttribute('min');
  const maxQty = +quantitySelectorInput.getAttribute('max');

  if (
    (target.classList.contains('cm_package_variant-quantity_button_minus') && currentQty <= minQty) ||
    (target.classList.contains('cm_package_variant-quantity_button_plus') && currentQty >= maxQty)
  ) {
    return;
  }

  const newQty = target.classList.contains('cm_package_variant-quantity_button_plus')
    ? currentQty + 1
    : currentQty - 1;

  quantitySelectorInput.setAttribute('value', newQty);
  quantitySelectorInput.value = newQty;

  window.Convermax.syncQtySelectorButtons(quantityContainer);

  const cartEl = target.closest('.cm_package_variant-cart');
  window.Convermax.updatePackageTotalPrice(cartEl, variant, variantRear, newQty, context);
};

window.Convermax.switchPriceMode = (e) => {
  if (!window.Convermax.Shopify.getCompanyId()) {
    return;
  }

  const pacakgeEl = e.target.closest('.cm_package_variant');
  if (pacakgeEl) {
    const priceMode = pacakgeEl.getAttribute('data-price-mode');
    pacakgeEl.setAttribute('data-price-mode', priceMode === 'retail' ? 'b2b' : 'retail');
  }
};

window.Convermax.showSelectedOption = (e) => {
  const currentValue = e.target.value;
  const parentEl = e.target.closest('.cm_package_item');
  parentEl.querySelectorAll('.cm_package_variant').forEach((el) => {
    el.classList.toggle('cm_hide', currentValue !== el.getAttribute('data-option-id'));
  });
};

window.Convermax.getFinishSelection = () => {
  const finishFacet = window.Convermax.getSearchResponse?.()?.facets?.get('convermax_finish_options');

  if (!finishFacet) {
    return [];
  }

  return finishFacet.selection?.map((f) => f.value);
};

window.Convermax.getOptionAvailableFinishes = (frontOption, rearOption, finishSelection) => {
  const clean = (list) =>
    Array.isArray(list) ? list.filter((v) => v && v !== 'Raw (Finish must be chosen)') : [];

  const frontFinishes = clean(frontOption?.convermax_finish_options);
  const rearFinishes = clean(rearOption?.convermax_finish_options);

  let finishes = frontFinishes;

  if (rearOption) {
    if (!frontFinishes.length) {
      finishes = rearFinishes;
    } else if (rearFinishes.length) {
      const rearSet = new Set(rearFinishes.map((v) => v.toLowerCase()));
      const intersection = frontFinishes.filter((v) => rearSet.has(v.toLowerCase()));
      if (intersection.length) {
        finishes = intersection;
      }
    }
  }

  if (!Array.isArray(finishSelection) || finishSelection.length === 0) {
    return finishes;
  }

  const selectedSet = new Set(finishSelection.map((v) => v.toLowerCase()));
  return finishes.sort(
    (a, b) => Number(selectedSet.has(b.toLowerCase())) - Number(selectedSet.has(a.toLowerCase())),
  );
};

window.Convermax.submitPackagesFacetsSelection = (event, selection, hasSelection) => {
  const dialogEl = event.target.closest('.cm_dialog');
  const isTireRequestPanel = dialogEl.classList.contains('TireFacetsDialog');
  const redirectPath = isTireRequestPanel ? '/collections/tires' : '/collections/wheels';

  const proccesedSelection =
    isTireRequestPanel && !hasSelection ? window.Convermax.tireFacetsPreselect : selection;

  if (Object.values(proccesedSelection).filter(Boolean).length === 0) {
    return;
  }

  const includeRear = dialogEl.querySelector('#toggle').checked;

  const frontFields = {};
  const rearFields = {};
  for (const [key, value] of Object.entries(proccesedSelection)) {
    if (!value) {
      continue;
    }
    if (key.endsWith('_rear')) {
      rearFields[key] = value;
    } else {
      frontFields[key] = value;
    }
  }

  const queryFields = includeRear ? { ...frontFields, ...rearFields } : frontFields;

  const loc = `${redirectPath}?${Object.entries(queryFields)
    .map((f) => f.join('/'))
    .join('/')}`;

  window.location.assign(loc);
};

const cartUrls = window.Convermax.Shopify?.getCartUrls();

function showHideAddToCartButtonSpinner(spinnerEl, textEl, show) {
  if (!spinnerEl && !textEl) {
    return;
  }

  if (show) {
    textEl.style.display = 'none';
  } else {
    textEl.style.display = 'block';
  }

  spinnerEl.classList.toggle('cm_hide');
}

async function isProductsAddedToCart(items, productIds, cartEl) {
  try {
    await ajaxAddToCart(items, cartEl);
    return true;
  } catch (err) {
    // If add failed, check cart manually
    console.error('Add to cart failed for ids', productIds.join('|'), err);

    try {
      const cartResponse = await ajaxGetCartItems();
      const parsedCart = await cartResponse.json();
      const inCartIds = (parsedCart?.items ?? []).map((i) => +i.variant_id);

      const idsToCheck = productIds.filter(Boolean).map((id) => +id);
      const allInCart = idsToCheck.length > 0 && idsToCheck.every((id) => inCartIds.includes(id));

      if (allInCart) {
        return true;
      }
    } catch (err) {
      console.warn('Failed to read cart after add attempt', err);
    }
  }

  return false;
}

async function ajaxAddToCart(items, cartEl) {
  const cartMessageEl = cartEl?.querySelector('.cm_cart-message');
  if (cartMessageEl) {
    cartMessageEl.innerText = '';
    cartMessageEl.classList.add('cm_hide');
  }

  const response = await fetch(`${cartUrls.add}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });

  if (!response.ok) {
    const errorResponse = await response.json().catch(() => null);
    let message = ``;
    if (errorResponse) {
      if (errorResponse.message !== errorResponse.description) {
        message = `${errorResponse.message}: ${errorResponse.description}`;
      } else {
        message = errorResponse.message;
      }
    }

    if (message) {
      console.error('Add to cart failed', message);

      if (cartMessageEl) {
        cartMessageEl.innerText = message;
        cartMessageEl.classList.remove('cm_hide');
      }
    }

    throw response;
  }

  return response;
}

async function ajaxGetCartItems() {
  const response = await fetch(`${cartUrls.root}.js`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw response;
  }

  return response;
}

const addWheelTireAccessories = (frontProduct, quantity = 1, parentLineKey) => {
  const items = [];

  if (!frontProduct) {
    return items;
  }

  const parentId = frontProduct.variant_ids?.[0];
  const requiredAddons = frontProduct.required_addons;

  if (!requiredAddons) {
    return items;
  }

  for (const addon of requiredAddons) {
    const item = {
      id: parseInt(addon.VariantId),
      quantity: addon.IsPerWheel ? quantity : 1,
      properties: addon.Properties ?? {},
    };

    if (parentLineKey) {
      item.parent_line_key = parentLineKey;
    } else if (parentId) {
      item.parent_id = parseInt(parentId);
    }

    items.push(item);
  }

  return items;
};

const getPackageRedirectPath = (variants) => {
  if (!variants || variants.length === 0) {
    return;
  }
  const [frontProduct, rearProduct] = variants;
  const frontProductId = frontProduct.id;
  const rearProductId = rearProduct?.id;

  if (!frontProductId) {
    console.error('Missing front product variant id', { variants });
    return;
  }

  const isWheelTirePackagePageLastStep =
    isWheelTirePackagePage && window.location.search.includes('WheelProductID');
  return isWheelTirePackagePageLastStep
    ? '/cart'
    : isWheelTirePackagePage
      ? window.Convermax.getVehicleUrl(
          `${window.location.pathname}?WheelProductID/${frontProductId}${rearProductId ? `:${rearProductId}` : ''}`,
        )
      : null;
};

const getLaborCost = (size) => {
  const numSize = Number.parseInt(size, 10);

  switch (true) {
    case [14, 15, 16].includes(numSize):
      return 30;
    case [17, 18, 19].includes(numSize):
      return 35;
    case [20, 21, 22].includes(numSize):
      return 40;
    case [23, 24].includes(numSize):
      return 50;
    case numSize === 26:
      return 100;
    case [28, 30].includes(numSize):
      return 200;
    default:
      return 0;
  }
};

const addVariantsToCart = (variants, fitmentOrderNotes) => async (e) => {
  e?.preventDefault?.();
  e?.stopPropagation?.();

  const quantityInput = e.target?.parentElement?.querySelector('.cm_package_variant-quantity_input');
  const cartEl = e.target?.closest('.cm_package_variant-cart');

  if (!variants || variants.length === 0 || !quantityInput) {
    return;
  }

  const [frontProduct, rearProduct] = variants;
  const frontProductId = frontProduct?.variant_ids?.[0];
  const rearProductId = rearProduct?.variant_ids?.[0];

  if (!frontProductId) {
    console.error('Missing front product variant id', { variants });
    return;
  }

  const packageRedirectPath = getPackageRedirectPath(variants);

  const baseProperties = {};
  if (fitmentOrderNotes) {
    baseProperties.Fitment = fitmentOrderNotes;
  }

  const finishSelectEl = e?.target
    ?.closest('.cm_package_variant-cart')
    ?.querySelector('select.cm_package_variant-finish-select');
  if (finishSelectEl) {
    baseProperties.Finish = finishSelectEl.value;
  }

  if (isWheelTirePackagePage) {
    baseProperties._isPackageFlow = 'Yes';
  }

  const frontProperties = { ...baseProperties };
  const rearProperties = { ...baseProperties };

  if (frontProduct.wheel_tire_diameter) {
    const frontLaborCost = getLaborCost(frontProduct.wheel_tire_diameter);
    if (frontLaborCost > 0) {
      frontProperties._laborCost = `$${frontLaborCost}`;
    }
  }

  if (rearProduct && rearProduct.wheel_tire_diameter) {
    const rearLaborCost = getLaborCost(rearProduct.wheel_tire_diameter);
    if (rearLaborCost > 0) {
      rearProperties._laborCost = `$${rearLaborCost}`;
    }
  }

  const quantity =
    window.Convermax.syncQtySelectorButtons(quantityInput.parentElement) ??
    Number.parseInt(quantityInput.value, 10);
  const totalAddToCartItemsQuantity = rearProduct ? quantity * 2 : quantity;

  const wheelItems = [
    { id: parseInt(frontProductId), quantity, properties: frontProperties },
    ...(rearProductId ? [{ id: parseInt(rearProductId), quantity, properties: rearProperties }] : []),
  ];

  const buttonSpinner = e.target.querySelector('.cm_spinner');
  const buttonText = e.target.querySelector('.cm_add-to-cart-text');
  if (buttonSpinner && !buttonSpinner.classList.contains('cm_hide')) {
    return;
  }

  showHideAddToCartButtonSpinner(buttonSpinner, buttonText, true);

  let addedToCart = false;

  try {
    const response = await ajaxAddToCart(wheelItems, cartEl);
    const addResult = await response.json();
    addedToCart = true;

    const addedItems = Array.isArray(addResult?.items) ? addResult.items : [addResult];
    const parentLineKey = addedItems.find((item) => +item.variant_id === +frontProductId)?.key;
    const accessoryItems = parentLineKey
      ? addWheelTireAccessories(frontProduct, totalAddToCartItemsQuantity, parentLineKey)
      : [];

    if (frontProduct.required_addons?.length > 0 && !parentLineKey) {
      console.error('Parent line key not found for accessories', frontProductId);
    }

    if (accessoryItems.length > 0) {
      try {
        await ajaxAddToCart(accessoryItems, cartEl);
      } catch (err) {
        console.error('Add to cart failed for accessories', err);
      }
    }
  } catch (err) {
    console.error(
      'Add to cart failed for ids',
      [frontProductId, rearProductId].filter(Boolean).join('|'),
      err,
    );
  }

  showHideAddToCartButtonSpinner(buttonSpinner, buttonText, false);

  if (addedToCart) {
    if (!window.cart?.cartDrawerEnabled || packageRedirectPath) {
      window.location.assign(packageRedirectPath || '/cart');
    } else {
      window.cart?.getCart();
      window.cart?.scrollToCartTop();
    }
  }
};
window.Convermax.addVariantsToCart = addVariantsToCart;

window.Convermax.getVehicleLabelWithoutChassis = (vehicle = {}) => {
  return Object.entries(vehicle)
    .filter(([field]) => field !== 'Chassis')
    .map(([, value]) => value)
    .join(' ');
};

const addProductsToCart = (id, quantity, fitmentOrderNotes) => async (e) => {
  e?.preventDefault?.();
  e?.stopPropagation?.();

  if (!id || !quantity) {
    return;
  }

  const properties = {};
  if (fitmentOrderNotes) {
    properties.Fitment = fitmentOrderNotes;
  }

  const items = [{ id, quantity, properties }];

  const buttonSpinner = e.target.querySelector('.cm_spinner');
  const buttonText = e.target.querySelector('.cm_add-to-cart-text');
  if (buttonSpinner && !buttonSpinner.classList.contains('cm_hide')) {
    return;
  }

  showHideAddToCartButtonSpinner(buttonSpinner, buttonText, true);
  const addedToCart = await isProductsAddedToCart(items, [id]);
  showHideAddToCartButtonSpinner(buttonSpinner, buttonText, false);

  if (addedToCart) {
    window.cart?.getCart();
    window.cart?.scrollToCartTop();
  }
};
window.Convermax.addProductsToCart = addProductsToCart;

function getLocalPreselection(pageType, defaults) {
  const pagePreselection = isWinterPackagePage
    ? [{ field: 'category', term: 'Winter-Wheels', treeLevel: 0 }]
    : (defaults.getter(pageType) ?? []);

  return [
    ...pagePreselection,
    // have some magic with NestedThenParentStrategy
    // will create task and debug, why we don't narrow parrents with one field
    // and need to duplicate it in request
    { field: 'IsStaggeredMasterProduct', term: 'True' },
    { field: 'IsStaggeredMasterProduct1', term: 'True' },
  ];
}

const baseUpdateCallback = shopifyGenericDefaults.Widgets.find(
  (w) => w.name === 'SearchResult',
)?.updateCallback;

const updateCallback = () => {
  baseUpdateCallback?.();
  packageSelectsUpdateCallback();
};

window.Convermax.packageSelectsOnChange = packageSelectsOnChange;

const REQUIRED_ADDONS_DATASET_KEY = 'requiredAddons'; // form.dataset.requiredAddons

const getProductFormQuantityInput = (form) =>
  form.querySelector('.quantity__selector.quantity__input, input[name="quantity"]');

const getProductFormQuantity = (form) => {
  const quantity = Number.parseInt(getProductFormQuantityInput(form)?.value ?? '1', 10);
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
};

const syncAcceleratedCheckoutAddonsQuantity = (form) => {
  const quantity = getProductFormQuantity(form);
  const addonsContainer = form.querySelector('[data-cm-accelerated-addons]');

  addonsContainer?.querySelectorAll('[data-cm-is-per-wheel]').forEach((input) => {
    input.value = input.dataset.cmIsPerWheel === 'true' ? quantity : 1;
  });
};

const appendAcceleratedCheckoutInput = (container, name, value) => {
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = String(value);
  container.appendChild(input);
  return input;
};

// Accelerated checkout bypasses the regular Add to Cart click handler, so expose
// server-provided required add-ons through Shopify's supported product-form fields.
const setAcceleratedCheckoutAddons = (form, items) => {
  form.querySelector('[data-cm-accelerated-addons]')?.remove();

  if (!Array.isArray(items) || items.length === 0) {
    return;
  }

  const parentId = form.querySelector('input[name="id"]')?.value;
  if (!parentId) {
    return;
  }

  const container = document.createElement('div');
  container.hidden = true;
  container.dataset.cmAcceleratedAddons = '';

  const occupiedIndexes = Array.from(
    form.querySelectorAll('input[name^="shopify-additional-items["]'),
    (input) => Number.parseInt(input.name.match(/^shopify-additional-items\[(\d+)\]/)?.[1] ?? '0', 10),
  );
  const firstAvailableIndex = Math.max(0, ...occupiedIndexes) + 1;

  items.forEach((item, offset) => {
    const index = firstAvailableIndex + offset;
    const prefix = `shopify-additional-items[${index}]`;

    appendAcceleratedCheckoutInput(container, `${prefix}[id]`, item.id);

    const quantityInput = appendAcceleratedCheckoutInput(container, `${prefix}[quantity]`, 1);
    quantityInput.dataset.cmIsPerWheel = String(!!item.isPerWheel);

    appendAcceleratedCheckoutInput(container, `${prefix}[parent_id]`, parentId);

    Object.entries(item.properties ?? {}).forEach(([key, value]) => {
      appendAcceleratedCheckoutInput(container, `${prefix}[properties][${key}]`, value);
    });
  });

  form.appendChild(container);
  syncAcceleratedCheckoutAddonsQuantity(form);

  const productQuantityInput = getProductFormQuantityInput(form);
  if (productQuantityInput && productQuantityInput.dataset.cmAcceleratedAddonsListener !== 'true') {
    productQuantityInput.dataset.cmAcceleratedAddonsListener = 'true';
    productQuantityInput.addEventListener('input', () => syncAcceleratedCheckoutAddonsQuantity(form));
    productQuantityInput.addEventListener('change', () => syncAcceleratedCheckoutAddonsQuantity(form));
  }
};

const setRequiredAddons = (form, items) => {
  if (!Array.isArray(items) || items.length === 0) {
    delete form.dataset[REQUIRED_ADDONS_DATASET_KEY];
    form.dataset.hubRingsHandled = 'false';
    return;
  }

  form.dataset[REQUIRED_ADDONS_DATASET_KEY] = JSON.stringify(items);
  form.dataset.hubRingsHandled = 'false';
};

const getRequiredAddons = (form) => {
  const raw = form.dataset[REQUIRED_ADDONS_DATASET_KEY];
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const onVerifyFitmentResponseReceived = (response) => {
  const form = document.querySelector('form[data-product-form]');
  const [product] = response.Items;

  if (!form || !product || form.querySelector('button[data-add-to-cart]:disabled')) {
    return;
  }

  if (isWheelTirePackageLastStep) {
    const input = document.createElement('input');
    input.className = 'cm_is-package-flow';
    input.type = 'hidden';
    input.name = 'properties[_isPackageFlow]';
    input.value = 'Yes';
    form.appendChild(input);
  }

  const addons = Array.isArray(product.required_addons) ? product.required_addons : [];

  const items = addons
    .map((a) => {
      const variantId = a?.VariantId;
      if (!variantId) {
        return null;
      }

      const properties = a?.Properties ?? {};

      return {
        id: parseInt(variantId),
        quantity: 1,
        isPerWheel: a.IsPerWheel,
        properties,
      };
    })
    .filter(Boolean);

  setRequiredAddons(form, items);
  setAcceleratedCheckoutAddons(form, items);
};

const addRequiredAddonsAsync = async (e) => {
  const formButton = e.currentTarget;
  const form = formButton.closest('form[data-product-form]');

  if (!form || form.dataset.hubRingsHandled === 'true') {
    return;
  }

  const items = getRequiredAddons(form);
  if (items.length === 0) {
    return;
  }

  items.forEach((item) => {
    item.quantity = item.isPerWheel ? getProductFormQuantity(form) : 1;
  });

  e.preventDefault();
  e.stopPropagation();

  form.dataset.hubRingsHandled = 'true';

  try {
    await ajaxAddToCart(items);
  } catch (err) {
    console.error('Add to cart failed for addons');
  }

  formButton.click();
};

const AfterInit = () => {
  shopifyGenericDefaults.AfterInit?.();

  if (window.Convermax.Shopify.pageType === 'product') {
    const formButton = document.querySelector('form[data-product-form] .product__submit__add');
    if (formButton) {
      formButton.addEventListener('click', addRequiredAddonsAsync);
    }
  }
};

const vehicleSelectionShouldNotBeLoaded = () => {
  const { search } = window.location;
  return (
    search &&
    !baseFitmentFields.some((f) => search.includes(f)) &&
    (wheelsFields.some((f) => search.includes(f)) || tiresFields.some((f) => search.includes(f)))
  );
};

export default {
  includes: ['shopify-generic'],
  ...shopifyGenericDefaults,
  infiniteScrollEnabled: false,
  doAutocompleteRedirect: true,
  getLocalPreselection,
  AfterInit,
  SearchRequestDefaults: {
    ...shopifyGenericDefaults.SearchRequestDefaults,
    extra: {
      ...shopifyGenericDefaults.SearchRequestDefaults.extra,
      isWheelTirePackageLastStep,
      isWinterPackagePage,
    },
  },
  product: {
    ...shopifyGenericDefaults.product,
    searchResultsImageWidth: 800,
    searchResultsImageHeight: 800,
  },
  fitmentSearch: {
    ...shopifyGenericDefaults.fitmentSearch,
    forceVehicleSelection:
      shopifyGenericDefaults.fitmentSearch.forceVehicleSelection || isWheelTirePackagePage,
    onVerifyFitmentResponseReceived,
    onVerifyFitmentInitResponseReceived: onVerifyFitmentResponseReceived,
    doNotLoadSelectedVehicle: vehicleSelectionShouldNotBeLoaded(),
  },
  pagination: {
    centralRange: 1,
    edgeRange: 1,
  },
  Widgets: [
    ...shopifyGenericDefaults.Widgets.filter(
      (w) =>
        ![
          'SearchResult',
          'FacetBarWheels',
          'FacetBarTires',
          'SearchVehicleWidget',
          'HomeVehicleWidget_tab',
        ].includes(w.name),
    ),
    {
      name: 'SearchResult',
      infiniteScrollDisabled: !!window.Convermax.config?.infiniteScrollDisabled,
      instantInfiniteScroll: !!window.Convermax.config?.instantInfiniteScroll,
      updateCallback,
    },
    {
      name: 'SearchVehicleWidget',
      type: 'VehicleWidget',
      fields: !!window.Convermax.config?.extraFieldsAsFilters && baseFitmentFields,
      facetToggleConfig: {
        initToggled: window.location.pathname === '/collections/tires',
        toggledFacet: { field: 'show_only_oem_options', term: 'True' },
      },
      isVehicleSelectionIsolated: !!window.Convermax.config?.isVehicleSelectionIsolated,
    },
    {
      name: 'HomeVehicleWidget_tab',
      type: 'VehicleWidget',
      location: {
        selector: '#cm-home-ymm',
        class: 'cm_vehicle-widget__tabs',
      },
      template: 'fitmentSearch/homeVehicleWidget',
      columnBreakpoint: 795,
    },
    {
      name: 'FacetBarWheels',
      type: 'FacetBar',
      fields: wheelsFields,
      minFacetCount: 2,
    },
    {
      name: 'FacetBarWheelsPackageRear',
      type: 'FacetBar',
      fields: wheelsPackagesRearFields,
      minFacetCount: 2,
    },
    {
      name: 'FacetBarTires',
      type: 'FacetBar',
      fields: tiresFields,
      minFacetCount: 2,
    },
    {
      name: 'FacetBarTiresPackageRear',
      type: 'FacetBar',
      fields: tiresPackagesRearFields,
      minFacetCount: 3,
      showAllAlways: true,
    },
    {
      name: 'StaggeredTireFacetPanel',
      type: 'RequestPanel',
      needInitRequest: true,
      isAutoRedirectDisabled: true,
      selectFields: [...tiresFields, ...tiresPackagesRearFields],
      requestExtra: {
        isTirePackageFacetPanel: true,
      },
      redirectUrl: '/collections/tires',
    },
    {
      name: 'StaggeredWheelFacetPanel',
      type: 'RequestPanel',
      needInitRequest: true,
      isAutoRedirectDisabled: true,
      selectFields: [...wheelsFields, ...wheelsPackagesRearFields],
      requestExtra: {
        isWheelPackageFacetPanel: true,
      },
      redirectUrl: '/collections/wheels',
    },
    {
      name: 'TireFacetsDialog',
      type: 'ContextDialog',
      template: 'TireFacetsDialog',
    },
    {
      name: 'WheelFacetsDialog',
      type: 'ContextDialog',
      template: 'WheelFacetsDialog',
    },
    {
      name: 'UpsizeDownsizeFacetPanel',
      type: 'FacetPanel',
      fields: ['wheel_tire_diameter', 'wheel_tire_diameter_rear'],
      showAllAlways: true,
      disableCollapse: true,
    },
    {
      name: 'WheelTireVehicleInfo',
      visibleIf: () => false, // waiting on redisign vehicle info, adn show only OEM option
    },
  ],
};
